import { Location } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '@interfaces/user';
import { OnboardingStatus } from '@pages/onboarding/onboarding.module';
import { WebsocketService } from '@services/websocket.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { BillingService } from './billing.service';
import { NavigationService } from './navigation.service';
import { TokenService } from './token.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' }),
    withCredentials: true,
  };

  public returnURL!: string | null;

  constructor(
    private http: HttpClient,
    private spinner: NgxSpinnerService,
    private tokenService: TokenService,
    private apiService: ApiService,
    private navigationService: NavigationService,
    private userService: UserService,
    private billingService: BillingService,
    private location: Location,
    private readonly ws: WebsocketService,
  ) {}

  /**
   * Initializes the user data and company subscription data.
   * @returns An observable that emits a boolean indicating if the initialization was successful.
   */
  initialize(): Observable<boolean> {
    const currentPath = this.location.path() || '/';
    const authRoutes = ['logout', 'forgot-password', 'reset-password', 'verify-email', 'register'];
    if (authRoutes.some((route) => currentPath.includes(route))) {
      return of(true);
    }

    return this.refreshToken().pipe(
      tap((res) => {
        if (res?.access) {
          this.setJwtToken(res.access);
        }
      }),
      switchMap((res: any) => this.userService.getUserProfile(res.user.uuid)),
      switchMap((userData) => {
        this.userService.setUserData(userData);
        const companyUuid = userData.company.uuid;

        return this.billingService.getCompanySubscription(companyUuid).pipe(
          tap((res) => {
            this.userService.companySubscription.set(res.subscription);
            this.userService.companySubscriptionObject.set(res);
          }),
          map(() => true),
        );
      }),
      tap(() => {
        switch (this.userService.getUserData()?.onboarding_status) {
          case OnboardingStatus.STEP1:
            this.navigationService.navigateToOnboardingStepOne();
            break;
          case OnboardingStatus.STEP2:
            this.navigationService.navigateToOnboardingStepTwo();
            break;
          case OnboardingStatus.STEP3:
            this.navigationService.navigateToOnboardingStepThree();
            break;
          case OnboardingStatus.STEP4:
            this.navigationService.navigateToOnboardingStepFour();
            break;
          case OnboardingStatus.STEP5:
            this.navigationService.navigateToOnboardingStepFive();
            break;
          default:
            break;
        }
      }),
      catchError((error) => {
        this.userService.clearUserData();
        this.navigationService.navigateToLogin();
        return of(false);
      }),
    );
  }

  /**
   * Signs in a user.
   * @param user The user to sign in.
   * @returns An observable that emits the response from the server.
   */
  signIn = (user: User): Observable<any> =>
    this.http.post<any>(this.apiService.getLoginUrl(), user, this.httpOptions).pipe(catchError(this.handleError));

  /**
   * Switch to a different workspace.
   * @param {email: string, company: Company} The name of the company to switch to.
   * @returns An observable that emits the response from the server.
   */
  switchWorkspace = (data: { email: string; company: { uuid: string } }): Observable<any> =>
    this.http
      .post<any>(this.apiService.getSwitchWorkspaceUrl(), data, this.httpOptions)
      .pipe(catchError(this.handleError));

  /**
   * Returns a boolean indicating whether the user is currently logged in or not.
   * @returns {boolean} - True if the user is logged in, false otherwise.
   */
  get isLoggedIn(): boolean {
    let user = this.userService.getUserData();
    return user !== null ? true : false;
  }

  /**
   * Logout user by calling api
   * @returns An observable that emits the response from the server.
   */

  logoutUser = (): Observable<any> => this.http.post<any>(this.apiService.logoutURL(), {}, this.httpOptions);

  /**
   * Logout a specific device.
   * @param deviceId The id of the device to logout.
   * @returns An observable that emits the response from the server.
   */
  logoutDevice = (deviceId: string): Observable<any> =>
    this.http.post<any>(this.apiService.logoutURL(), { devices: [{ uuid: deviceId }] }, this.httpOptions);

  /**
   * Logout all devices.
   * @returns An observable that emits the response from the server.
   */
  logoutAllDevices = (): Observable<any> =>
    this.http.post<any>(this.apiService.logoutURL(), { devices: ['__all__'] }, this.httpOptions);

  /**
   * Clears the access token, removes user data from local storage, navigates to the login page and hides the spinner.
   */
  doLogout(): void {
    this.userService.clearUserData();
    this.tokenService.clearAccessToken();
    this.ws.closeWebSocket();
    this.spinner.hide();
    this.navigationService.navigateToLogin();
  }

  /**
   * Handles HTTP errors and returns an observable that emits an error notification.
   * @param error - The HttpErrorResponse object to handle.
   * @returns An observable that emits an error notification.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let msg = '';
    if (error.error instanceof ErrorEvent) {
      // client-side error
      msg = error.error.message;
    } else {
      // server-side error
      msg = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => new Error(msg));
  }

  /**
   * Stores the access token, refresh token, and user data in the local storage.
   * @param userData An object containing the access token, refresh token, and user data.
   */
  storeTokens(access: string): void {
    this.tokenService.setAccessToken(access);
  }

  /* interface between token service and auth service */
  getJwtToken = (): string | null => this.tokenService.getAccessToken();

  setJwtToken = (token: string): void => this.tokenService.setAccessToken(token);

  clearJwtToken = (): void => this.tokenService.clearAccessToken();

  refreshToken = (): Observable<any> => this.tokenService.refreshToken();

  /**
   * Checks if the JWT token is expired
   * @param token The JWT token to check
   * @returns true if the token is expired or invalid, false otherwise
   */
  isTokenExpired(token: string | null): boolean {
    if (!token) {
      return true;
    }

    try {
      const parts = token.split('.');
      if (parts.length < 3) {
        // Malformed token
        return true;
      }
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }
}
